import { AptevaClient, pickBaseURL, pickKioskKey } from "@apteva/web-sdk";

const APTEVA_PROD_URL = "https://agents.schwartzindustries.com";
const APTEVA_WRITE_KEY = "wk_live_761b889de7d72db54fbfce9c6fddbe7bb161";
const APTEVA_CRM_KEY = "pk_43dc0357b901d56f51ae7a6478f85a7d1b9ea3afa80b8add";
const APTEVA_PROJECT_ID = "1778674017370-e3e4a374463047d2";
const LEAD_SOURCE = "schwartz-ai-partners-site";

declare global {
  interface Window {
    apa?: (eventName: string, properties?: Record<string, string | number | boolean | undefined>) => void;
  }
}

interface CRMContact {
  id: number;
}

interface CRMUpsertResult {
  contact?: CRMContact;
  was_created?: boolean;
}

export interface ContactLeadPayload {
  name: string;
  email: string;
  company: string;
  phone: string;
  companySize: string;
  priorityNeed: string;
  message: string;
  pagePath: string;
  industry?: string;
  plan?: string;
  preferredContact?: string;
  projectTimeline?: string;
  sourceSurface?: string;
  locale?: string;
}

function aptevaClient() {
  return new AptevaClient({
    baseURL: pickBaseURL(APTEVA_PROD_URL),
    apiKey: pickKioskKey(APTEVA_CRM_KEY || APTEVA_WRITE_KEY),
  });
}

function splitName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function tagFromNeed(need: string) {
  return need
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined>
) {
  if (typeof window === "undefined" || typeof window.apa !== "function") return;
  window.apa(eventName, properties);
}

export async function saveContactLeadToCRM(payload: ContactLeadPayload) {
  const email = payload.email.trim();
  if (!email) {
    throw new Error("Email is required");
  }

  const { firstName, lastName } = splitName(payload.name);
  const crm = aptevaClient().app("crm");
  const tags = [
    "schwartz-ai-partners-lead",
    "ai-consulting-lead",
    "crm-web-signup",
    payload.sourceSurface ? `surface-${tagFromNeed(payload.sourceSurface)}` : undefined,
    payload.industry ? `industry-${tagFromNeed(payload.industry)}` : undefined,
    payload.plan ? `plan-${tagFromNeed(payload.plan)}` : undefined,
    payload.priorityNeed ? `need-${tagFromNeed(payload.priorityNeed)}` : undefined,
  ].filter((tag): tag is string => Boolean(tag));

  const result = await crm.tool<CRMUpsertResult>("contacts_upsert_by_channel", {
    _project_id: APTEVA_PROJECT_ID,
    kind: "email",
    value: email,
    defaults: {
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      display_name: payload.name.trim() || email,
      source: LEAD_SOURCE,
      tags,
      company: payload.company || undefined,
    },
    source: LEAD_SOURCE,
  });

  if (!result.contact?.id) {
    throw new Error("CRM did not return a contact id");
  }

  await crm.tool("contacts_log_activity", {
    _project_id: APTEVA_PROJECT_ID,
    contact_id: result.contact.id,
    kind: "system",
    occurred_at: new Date().toISOString(),
    source: LEAD_SOURCE,
    body: JSON.stringify(
      {
        event: "schwartz_ai_partners_strategy_requested",
        was_created: result.was_created ?? false,
        company: payload.company,
        phone: payload.phone,
        company_size: payload.companySize,
        priority_need: payload.priorityNeed,
        industry: payload.industry,
        plan: payload.plan,
        preferred_contact: payload.preferredContact,
        project_timeline: payload.projectTimeline,
        source_surface: payload.sourceSurface,
        locale: payload.locale,
        message: payload.message,
        page_path: payload.pagePath,
        tags,
      },
      null,
      2
    ),
  });

  return result;
}
